import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import { foldDayKpis, metaByIdFromDefs, sortDaysAsc } from "../../helpers/scoreAggregate";
import { UseCompanySubWorkspaces, UseCompanyWorkspaces, UseUsersFullData } from "../../hooks/company";
import { getCompanyHolidays } from "../../redux/company/actions";
import { matchesWidgetRef } from "../../helpers/widgetRef";
import { holidayDaySet, periodEndFor, periodWordFor, workingDaysBetween } from "../../helpers/workingDays";
import {
  fetchWorkspaceScores,
  monthsForWindow,
  resolveWindow,
  sumScores,
} from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";
import useLaborData, {
  hoursWorkedKpiOf,
  includesSubWorkspaces,
  isRealEmployee,
  rowsOf as laborRowsOf,
  workspaceIdsOf,
} from "../LaborMetrics/data";

/**
 * Revenue, Expenses and Labor over time — the P&L the owner actually reads.
 *
 * The identity the whole card rests on, and the reason nothing here can be
 * double-counted:
 *
 *   Net profit = Revenue − Labor − Expenses          (dollars)
 *   NP% + Expense% + Labor% = 100%                   (shares of revenue)
 *
 * Every typed expense can still carry a `kind` of "opex" (default) or
 * "payroll" — opex lands on the Expenses line; payroll is added ON TOP of the
 * live wage figure into Labor instead, so it is never double-counted against
 * wages already in Labor. Setup no longer offers a way to CREATE a "payroll"
 * row (Kelvin asked to simplify Setup to one plain "Add expense" input) —
 * every new row is "opex". A board with an older saved "payroll" row keeps
 * reading it correctly; the split just isn't user-facing any more.
 *
 * Where each of the three comes from:
 *
 *   Revenue   - the company's Total Revenue metric (auto), any metric you
 *               pick, or a typed figure, summed for this card's own window
 *   Labor     - the Payroll vs Revenue card this one is pointed at: its own
 *               departments, hours and pay rates, re-read for THIS window,
 *               plus any payroll expenses typed here. Typed instead when the
 *               board has no such card.
 *   Expenses  - typed here, a line per cost, each a dollar figure or a share
 *               of revenue
 *
 * Nothing in the app knows what rent costs, so expenses are the one input
 * that will always be typed. Everything else syncs.
 */

const rid = () => `e${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* -- Expenses ------------------------------------------------------------ */

/** `kind` decides which line a cost lands on - see the double-count note above. */
export const EXPENSE_KINDS = [
  {
    id: "opex",
    label: "Non-payroll",
    note: "Rent, fuel, insurance, software, materials - everything that isn't people. Lands on the Expenses line.",
  },
  {
    id: "payroll",
    label: "Payroll",
    note: "Employer taxes, benefits, workers' comp, a bonus pool - real payroll cost that hours x pay/hour can't see. Added on top of the Labor line, never to Expenses.",
  },
];

export const EXPENSE_UNITS = [
  { value: "$", label: "$ for the period" },
  { value: "/day", label: "$ per day" },
  { value: "%", label: "% of revenue" },
];

export const emptyExpense = (kind = "opex") => ({
  key: rid(),
  label: "",
  amount: "",
  unit: "$",
  kind,
});

export const expensesOf = (s) => (Array.isArray(s?.expenses) ? s.expenses.filter(Boolean) : []);

/**
 * What one typed expense comes to over a window.
 *
 * @param {object} item     the saved expense row
 * @param {number} revenue  the window's revenue (only "%" needs it)
 * @param {number} days     days in the window (only "/day" needs it)
 */
export const expenseAmount = (item, revenue, days) => {
  const amt = num(item?.amount);
  /* Negatives are allowed through on purpose - a rebate, a credit or a
     reimbursed cost is a real line on the sheet, and clamping it to zero would
     quietly overstate what the month cost. */
  if (!amt) return 0;
  if (item?.unit === "%") return revenue * (amt / 100);
  if (item?.unit === "/day") return amt * Math.max(1, days);
  return amt;
};

/* -- Goals --------------------------------------------------------------- */

/**
 * A goal per series, so "labor must stay under 40%" can actually turn the card
 * red. `dir` is which way is good: a cost has a ceiling ("max"), revenue and
 * profit have a floor ("min"). `warn` is the amber step before `limit` - left
 * empty it simply isn't drawn, and the tile goes straight from good to
 * breached.
 */
export const SERIES_KEYS = ["revenue", "expenses", "labor", "net"];

/* Revenue, Expenses and Labor — the three the sketch draws. Net profit reads
   as a tile by default and joins the chart only when it is asked for. */
export const DEFAULT_LINES = ["revenue", "expenses", "labor"];

const GOAL_DEFAULTS = {
  revenue: { on: false, dir: "min", unit: "$", warn: "", limit: "" },
  expenses: { on: false, dir: "max", unit: "%", warn: "", limit: "" },
  labor: { on: false, dir: "max", unit: "%", warn: "", limit: "" },
  net: { on: false, dir: "min", unit: "%", warn: "", limit: "" },
};

export const goalOf = (s, key) => ({ ...GOAL_DEFAULTS[key], ...((s?.goals || {})[key] || {}) });

const numOrNull = (v) => (v === "" || v == null || !Number.isFinite(Number(v)) ? null : Number(v));

/**
 * Where a figure sits against its goal: "ok" / "warn" / "bad" / null (no goal).
 *
 * Compared on whichever unit the goal was typed in - a ceiling set as "40%" is
 * about the share, one set as "$40,000" is about the money, and quietly
 * swapping between them is how a goal stops meaning anything.
 */
export const goalStatus = (goal, { amount, share }, elapsed = 1) => {
  if (!goal?.on) return null;
  const onShare = goal.unit === "%";
  const value = onShare ? share : amount;
  if (value == null || !Number.isFinite(value)) return null;

  /* A SHARE is already normalised - labor at 32% of revenue is 32% on the 3rd
     and on the 30th - so it is judged against the limit as typed.
     A MONEY figure is not. A month's revenue compared to a month's goal is
     always short until the month ends, so an untouched "$250,000" floor
     painted every tile red for 29 days out of 30 and said nothing; a "$100,000"
     ceiling did the reverse, sitting green all month and only going red once it
     was far too late to act. Both are answered by the same thing: pro-rate the
     limit by the working days GONE, so the tile reads "are we where we should
     be by now" - the tile's version of the pace line's question. */
  const e = Number(elapsed);
  const scale = onShare ? 1 : Math.max(0, Math.min(1, Number.isFinite(e) ? e : 1));
  /* Nothing has been worked yet, so there is nothing to be behind on. */
  if (scale <= 0) return null;

  const limit = numOrNull(goal.limit);
  const warn = numOrNull(goal.warn);
  const breached = (v) => (goal.dir === "min" ? value <= v * scale : value >= v * scale);
  if (limit != null && breached(limit)) return "bad";
  if (warn != null && breached(warn)) return "warn";
  if (limit == null && warn == null) return null;
  return "ok";
};

/** The goal's own figure in dollars, for the reference line on a $ chart. */
export const goalLineValue = (goal, revenue) => {
  const limit = numOrNull(goal?.limit);
  if (!goal?.on || limit == null) return null;
  if (goal.unit === "%") return revenue > 0 ? revenue * (limit / 100) : null;
  return limit;
};

/**
 * A goal's place on a DOLLAR chart, which is not the goal itself.
 *
 * `goalLineValue` above is a figure for the whole PERIOD - "$250,000 in
 * September". The chart plots one day at a time, so drawing that figure flat
 * across it is not merely optimistic, it wrecks the chart: the reference line
 * extends the y-axis to reach it (ifOverflow="extendDomain") and every real
 * line is squashed into the floor. Dividing by the period's working days
 * turns it into what ONE DAY has to do - the same arithmetic the pace line
 * runs, and the only reading of a monthly goal a daily chart can carry.
 *
 * Returns null on a week/month grain, where a bucket's share of the period is
 * not constant and no single flat value is right for every bucket. The tile
 * still reports the goal and its status; only the LINE is withheld.
 */
export const goalLinePerDay = (goal, revenue, grain, workingDays) => {
  if (grain !== "day" || !(workingDays > 0)) return null;
  const total = goalLineValue(goal, revenue);
  return total == null ? null : total / workingDays;
};

/* -- Formatting ---------------------------------------------------------- */

export const money = (value) => {
  const n = num(value);
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.round(Math.abs(n)).toLocaleString()}`;
};

/** Axis / tick money - "$48.2k" past a thousand, so ticks don't collide. */
export const moneyShort = (value) => {
  const n = num(value);
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  if (a >= 1000000) return `${sign}$${(a / 1000000).toFixed(a >= 10000000 ? 0 : 1)}M`;
  if (a >= 1000) return `${sign}$${(a / 1000).toFixed(a >= 10000 ? 0 : 1)}k`;
  return `${sign}$${Math.round(a)}`;
};

export const pct = (value) => {
  if (value == null || !Number.isFinite(value)) return "—";
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
};

/* -- Buckets ------------------------------------------------------------- */

export const GRAINS = [
  { id: "auto", label: "Automatic" },
  { id: "day", label: "Daily" },
  { id: "week", label: "Weekly" },
  { id: "month", label: "Monthly" },
];

/**
 * A point per day reads well over a month and turns to noise over a year, so
 * the default widens with the window instead of pinning one grain and hoping.
 */
export const grainFor = (from, to, want = "auto") => {
  if (want && want !== "auto") return want;
  const days = Math.max(1, moment(to).endOf("day").diff(moment(from).startOf("day"), "days") + 1);
  if (days <= 31) return "day";
  if (days <= 120) return "week";
  return "month";
};

/**
 * The buckets a window breaks into, each carrying the label its axis tick
 * shows. Weeks are Sunday-start (the app's own week everywhere else) and the
 * first and last are clipped to the window rather than spilling outside it -
 * a "1-15 March" range must not draw a week that reaches back into February.
 */
export const bucketsFor = (from, to, grain) => {
  const out = [];
  const unit = grain === "week" ? "week" : grain;
  const start = moment(from).startOf("day");
  const end = moment(to).endOf("day");
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return out;
  const sameMonth = start.isSame(end, "month") && start.isSame(end, "year");
  const cursor = start.clone().startOf(unit);
  /* Hard stop: a malformed range must never spin here. 800 is far past any
     grain a real window produces (a decade of months, two years of weeks). */
  while (cursor.isSameOrBefore(end) && out.length < 800) {
    const bFrom = moment.max(cursor.clone().startOf(unit), start);
    const bTo = moment.min(cursor.clone().endOf(unit), end);
    let label;
    if (grain === "month") label = bFrom.format("MMM");
    else if (grain === "week") label = bFrom.format("MMM D");
    else label = sameMonth ? String(bFrom.date()) : bFrom.format("MMM D");
    out.push({
      key: bFrom.format("YYYY-MM-DD"),
      label,
      full: grain === "month"
        ? bFrom.format("MMMM YYYY")
        : `${bFrom.format("MMM D")}${bTo.isSame(bFrom, "day") ? "" : ` – ${bTo.format("MMM D")}`}`,
      from: bFrom,
      to: bTo,
      days: bTo.diff(bFrom, "days") + 1,
    });
    cursor.add(1, unit);
  }
  return out;
};

/* -- Working days -------------------------------------------------------- */

/* holidayDaySet / workingDaysBetween / periodEndFor live in
   helpers/workingDays.js — Goal Progress paces against the same denominator
   and there must be exactly one definition of it. Re-exported here because
   this module is where every caller (and this card's tests) already import
   them from. */
export { holidayDaySet, workingDaysBetween, periodEndFor, periodWordFor };

/**
 * Each bucket's share of the window by CALENDAR length - the flat spread a
 * typed cost with no rostering behind it should draw (rent, fuel, insurance):
 * it doesn't stop accruing on a Saturday just because nobody clocked in, so
 * it stays flat across the weekend rather than dipping like Labor does.
 */
export const calendarShapeOf = (buckets) => {
  const span = (buckets || []).reduce((s, b) => s + (b.future ? 0 : b.days), 0) || 1;
  return (buckets || []).map((b) => (b.future ? 0 : b.days / span));
};

/**
 * Each bucket's share of the window, weighted by WORKING days.
 *
 * A cost with no daily shape of its own — a typed department, a salaried
 * month, a "$ per day" expense — has to be spread across the buckets somehow,
 * and spreading it by calendar days puts a full day's payroll on every
 * Saturday and Sunday. That is what drew the Labor line flat across weekends:
 * nobody was rostered, but a thirtieth of the month's wages landed there all
 * the same. Weighting by working days instead lands it only on the days the
 * company planned to trade, so a salaried department genuinely dips to its
 * baseline at the weekend — the same Mon–Fri-minus-holidays definition the
 * pace divisor and the company calendar already use (workingDaysBetween).
 *
 * This only ever moves money BETWEEN buckets. Every share still sums to 1, so
 * no total, tile or share-of-revenue figure on the card changes — a per-day
 * expense is still billed for the calendar days it ran for, it is just drawn
 * on the days somebody was there to run it.
 *
 * Live hours are untouched by any of this: a technician who logged a Saturday
 * really did work it, and that Saturday keeps its cost.
 *
 * Falls back to calendar length when a window contains no working days at all
 * (a Saturday-only custom range), where weekday weighting would zero the whole
 * chart rather than tell anyone anything.
 */
export const workShapeOf = (buckets, holidays) => {
  const skip = holidays instanceof Set ? holidays : holidayDaySet(holidays);
  const work = (buckets || []).map((b) => (b.future ? 0 : workingDaysBetween(b.from, b.to, skip)));
  const workSpan = work.reduce((s, v) => s + v, 0);
  if (workSpan > 0) return work.map((v) => v / workSpan);
  return calendarShapeOf(buckets);
};

/**
 * Folds each Saturday/Sunday/company-holiday day-bucket into the WORKING day
 * beside it, for the "working days only" axis: nothing is dropped, a folded
 * day's real revenue/hours/cost just draws on the point next to it instead of
 * its own, the same way a week-grain bucket already draws several days as one
 * point. `.days` grows to match, so calendarShapeOf's opex share follows the
 * money onto the surviving point rather than losing it.
 *
 * Direction matters and is decided by the bucket's OWN `.future` flag, set by
 * the caller before this runs: an already-happened weekend rolls BACKWARD
 * onto the working day before it; one still to come rolls FORWARD onto the
 * working day after it. Both are safe because a fold never reaches across the
 * `.future` line — the two directions only ever meet at the boundary, never
 * cross it. A leading non-future bucket with nothing kept yet to roll
 * backward onto (the window opens on a weekend or holiday) is left standing
 * rather than guessed onto either side.
 */
export const foldNonWorkingDays = (buckets, holidays) => {
  const skip = holidays instanceof Set ? holidays : holidayDaySet(holidays);
  const isNonWorking = (b) => {
    const dow = b.from.day();
    return dow === 0 || dow === 6 || skip.has(b.from.format("YYYY-MM-DD"));
  };
  const full = (from, to) => `${from.format("MMM D")}${to.isSame(from, "day") ? "" : ` – ${to.format("MMM D")}`}`;
  const out = [];
  let pendingFrom = null; // a future skip-bucket's `from`, waiting for the next kept bucket
  (buckets || []).forEach((b) => {
    if (isNonWorking(b)) {
      if (b.future) {
        pendingFrom = pendingFrom || b.from;
        return;
      }
      if (out.length) {
        const prev = out[out.length - 1];
        prev.to = b.to;
        prev.days = prev.to.diff(prev.from, "days") + 1;
        prev.full = full(prev.from, prev.to);
        return;
      }
      out.push({ ...b }); // nothing kept yet to roll this real day backward onto
      return;
    }
    const merged = { ...b };
    if (pendingFrom) {
      merged.from = pendingFrom;
      merged.days = merged.to.diff(merged.from, "days") + 1;
      merged.full = full(merged.from, merged.to);
      pendingFrom = null;
    }
    out.push(merged);
  });
  return out;
};

export const displayEndFor = (bucket, to) => {
  const end = moment(to);
  if (!end.isValid()) return end;
  return bucket === "month" ? end.clone().endOf("month") : end;
};

/** Which bucket a day row belongs to - a lookup, not a scan per row. */
const bucketIndexer = (buckets) => {
  const byDay = new Map();
  buckets.forEach((b, i) => {
    const cursor = b.from.clone().startOf("day");
    while (cursor.isSameOrBefore(b.to, "day")) {
      byDay.set(cursor.format("YYYY-MM-DD"), i);
      cursor.add(1, "day");
    }
  });
  return (day) => {
    const d = moment(day);
    return d.isValid() ? byDay.get(d.format("YYYY-MM-DD")) : undefined;
  };
};

/* -- Revenue source ------------------------------------------------------ */

/** The metric a card picked by hand, if it picked one. */
export const revenueKpiOf = (s) => (s?.revenueWorkspaceId && s?.revenueKpiId
  ? { workspaceId: String(s.revenueWorkspaceId), kpiId: String(s.revenueKpiId) }
  : null);

/** The typed figure - the fallback when no metric feeds revenue. */
export const revenueOf = (s) => num(s?.revenue);

export const revenueModeOf = (s) => {
  if (s?.revenueMode === "manual" || s?.revenueMode === "pick") return s.revenueMode;
  return "auto";
};

/**
 * The company's Total Revenue metric, wherever it lives. The same rule Payroll
 * vs Revenue uses (model.type, never a name match) so the two cards can never
 * disagree about which metric the word "revenue" points at.
 */
export const findTotalRevenueKpi = (workspaces, companyKpis) =>
  (workspaces || [])
    .map((w) => {
      const hit = (companyKpis?.[String(w._id)] || []).find(
        (k) => k && !k.isDraft && !k.disabled && (k.type === "total_revenue" || k?.model?.type === "total_revenue"),
      );
      return hit ? { workspaceId: String(w._id), kpiId: String(hit._id), name: hit.name || "Total Revenue" } : null;
    })
    .find(Boolean) || null;

/* -- Labor source -------------------------------------------------------- */

export const laborModeOf = (s) => (s?.laborMode === "manual" ? "manual" : "widget");

/**
 * Where the Labor line's own zero sits.
 *
 * "expenses" (the default) draws Labor measured UP FROM the Expenses line
 * instead of up from the axis: if expenses are $4,000 then $4,000 IS zero for
 * Labor, and a $1,000 labor bill draws at $5,000. Move expenses to $5,000 and
 * the whole Labor line lifts with it. That is how the business reads the
 * chart — labor is what is spent ON TOP of the running cost of the doors being
 * open — and it is why Labor can never cross Expenses in this mode: its floor
 * IS that line. "zero" is the plain reading, every line measured from the axis.
 */
export const laborBaseOf = (s) => (s?.laborBase === "zero" ? "zero" : "expenses");

/** Every Payroll vs Revenue card on this board, in the order they sit on it. */
export const payrollWidgetsOn = (boardWidgets) => (boardWidgets || []).filter((w) => w && w.comp === "labor");

/**
 * Which payroll card this one reads. An explicit pick always wins; with none
 * saved it falls to the only one on the board - but it never guesses between
 * two, because silently reading "whichever came first" is how a card ends up
 * quietly charting the wrong set of departments.
 */
export const resolveLaborWidget = (settings, boardWidgets) => {
  const choices = payrollWidgetsOn(boardWidgets);
  if (!choices.length) return { widget: null, choices, ambiguous: false, missing: true };
  const wanted = settings?.laborWidgetId ? String(settings.laborWidgetId) : "";
  const picked = wanted ? choices.find((w) => matchesWidgetRef(w, wanted)) : null;
  if (picked) return { widget: picked, choices, ambiguous: false, missing: false };
  /* A pick that no longer resolves is not the same as no pick: the card it
     named was removed or duplicated away, and quietly sliding onto a
     different one would change every number without saying so. */
  if (wanted) return { widget: null, choices, ambiguous: true, missing: false, stale: true };
  if (choices.length === 1) return { widget: choices[0], choices, ambiguous: false, missing: false };
  return { widget: null, choices, ambiguous: true, missing: false };
};

/* -- The hook ------------------------------------------------------------ */

/**
 * @param {object}  settings      this card's own saved options
 * @param {object}  laborOptions  the linked Payroll vs Revenue card's options (or null)
 * @param {string}  bucket        today | week | month | lastMonth | quarter | ytd | custom
 * @param {object} [custom]       { from, to } when bucket is "custom"
 */
export default function useProfitTrend(settings, laborOptions, bucket = "month", custom = null) {
  const companyId = useSelector((s) => s.authReducer.userDetails?.companyId);
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  /* The company calendar's own holidays, the same list the Calendar page and
     the Yeti's pace targets read - see workingDaysBetween above. */
  const holidays = useSelector((s) => s.companyReducer.holidays);
  const dispatch = useDispatch();
  /* Nothing else on a dashboard asks for them - the list is only fetched when
     somebody opens the Calendar page and edits it (CalendarContainer.jsx), so
     a board opened straight from login had an empty one. That was survivable
     while holidays only nudged a Yeti target; this card PRINTS the working-day
     count, and quietly reading 23 instead of 22 is a wrong number on screen.
     Asked for once per company; the saga caches it into redux for every other
     card that reads the same list. */
  useEffect(() => {
    if (companyId && !(holidays || []).length) dispatch(getCompanyHolidays());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  const allUsers = UseUsersFullData();
  const subWsByParent = UseCompanySubWorkspaces();
  const workspaces = UseCompanyWorkspaces(true) || [];

  /* In "auto" the Total Revenue metric is resolved FRESH every render rather
     than read from what Setup once stored. Setup's self-heal only runs while
     Setup is open, so a card added and never opened would otherwise have no
     revenue at all, and one configured before the metric was created would
     read $0 forever. A metric picked BY HAND is never re-resolved - that pick
     is the whole point of picking. */
  const revenueMode = revenueModeOf(settings);
  const autoRevKpi = useMemo(
    () => findTotalRevenueKpi(workspaces, companyKpis),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(workspaces.map((w) => w._id)), companyKpis],
  );
  let revKpi = null;
  if (revenueMode === "auto") revKpi = autoRevKpi;
  else if (revenueMode === "pick") revKpi = revenueKpiOf(settings);
  const typedRevenue = revenueOf(settings);
  const laborMode = laborModeOf(settings);
  const laborRows = laborMode === "widget" ? laborRowsOf(laborOptions) : [];
  /* The single real computation of hours/cost/pay-per-hour/live, for THIS
     card's own window — the same hook Payroll vs Revenue renders from, on
     that card's own saved options. See the "Labor, department by department"
     comment below for why this replaced a second, hand-rolled copy of the
     same math. */
  const laborData = useLaborData(laborMode === "widget" ? laborOptions : null, bucket, custom);

  const wsNameById = useMemo(() => {
    const m = {};
    workspaces.forEach((w) => { m[String(w._id)] = w.name; });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(workspaces.map((w) => [w._id, w.name]))]);

  /* Every workspace a department row counts: its own members plus their
     sub-workspaces, unless the row switched those off. Identical to Payroll vs
     Revenue's rule - the two cards have to agree on what a department IS, or
     their totals drift apart for no visible reason. */
  const memberWsOf = (ids, includeSubs = true) => ids.flatMap((wsId) => [
    { id: String(wsId), name: null, sub: false },
    ...(includeSubs
      ? (subWsByParent?.[String(wsId)] || []).map((w) => ({ id: String(w._id), name: w.name, sub: true }))
      : []),
  ]);

  /* Only workspaces we actually read a live figure from. Resolved fresh
     against companyKpis rather than trusting a saved hoursKpiId, for the same
     reason Payroll vs Revenue does it: that field only self-heals while its
     own Setup is open, so a metric swapped or recreated since would otherwise
     be read through a dead id forever. */
  const wsIds = useMemo(() => {
    const set = new Set();
    laborRows.forEach((r) => {
      memberWsOf(workspaceIdsOf(r), includesSubWorkspaces(r)).forEach((mw) => {
        if (hoursWorkedKpiOf(companyKpis, mw.id)) set.add(mw.id);
      });
    });
    if (revKpi) set.add(revKpi.workspaceId);
    return [...set];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(laborRows.map((r) => [workspaceIdsOf(r), includesSubWorkspaces(r)])),
    revKpi?.workspaceId,
    revKpi?.kpiId,
    subWsByParent,
    companyKpis,
  ]);

  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  const [scoresByWs, setScoresByWs] = useState({});
  const [loadedKey, setLoadedKey] = useState("");

  const months = useMemo(
    () => monthsForWindow(bucket === "year" ? "ytd" : bucket, custom),
    [bucket, customKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const fetchKey = `${companyId || ""}|${wsIds.join(",")}|${months.join(",")}`;
  const fetching = !!wsIds.length && loadedKey !== fetchKey;
  const hasScores = Object.keys(scoresByWs).length > 0;

  useEffect(() => {
    if (!companyId || !wsIds.length) {
      setScoresByWs({});
      setLoadedKey(fetchKey);
      return undefined;
    }
    let alive = true;
    Promise.all(
      wsIds.map((id) => fetchWorkspaceScores(companyId, id, months)
        .then((s) => [id, s])
        .catch(() => [id, []])),
    ).then((pairs) => {
      if (!alive) return;
      setScoresByWs(Object.fromEntries(pairs));
      setLoadedKey(fetchKey);
    });
    return () => { alive = false; };
  }, [companyId, fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    const { from, to } = resolveWindow(bucket === "year" ? "ytd" : bucket, custom);
    /* The chart draws to the end of the month; every figure is still added up
       to the end of today (displayEndFor above). `days` stays pinned to `to`
       for that reason - it is what a "$ per day" expense is billed for. */
    const axisTo = displayEndFor(bucket, to);
    const grain = grainFor(from, axisTo, settings?.grain);
    const holidaySet = holidayDaySet(holidays);
    const rawBuckets = bucketsFor(from, axisTo, grain)
      .map((b) => ({ ...b, future: b.from.isAfter(to) }));
    /* "Working days only": fold weekends/holidays away so the axis only shows
       days the company planned to trade. Only meaningful per-day - a week or
       month bucket already spans the weekend inside it. */
    const buckets = (grain === "day" && settings?.workingDaysOnly === true)
      ? foldNonWorkingDays(rawBuckets, holidaySet)
      : rawBuckets;
    const bucketOf = bucketIndexer(buckets);
    const days = Math.max(1, moment(to).endOf("day").diff(moment(from).startOf("day"), "days") + 1);
    /* Each LABOR bucket's share of the window by WORKING days, so a clipped
       first week never claims as much of a typed figure as a full one beside
       it, and a weekend claims none of it at all - see workShapeOf above for
       why the weekday weighting is what stops a salaried department drawing
       a flat line straight through every Saturday. A day that hasn't
       happened claims nothing either: otherwise switching the chart out to
       the end of the month would thin every typed cost across days nobody
       has traded. Opex (rent, fuel, insurance) has no rostering behind it -
       it keeps accruing on a Saturday whether or not anyone's there to spend
       it, so it stays on the CALENDAR shape below rather than dipping too. */
    const evenShape = workShapeOf(buckets, holidaySet);
    const calendarShape = calendarShapeOf(buckets);

    /* Period totals come from the SAME fold Payroll vs Revenue runs (rate-like
       metrics kept rather than summed, composites recomputed), so this card's
       Labor figure is that card's figure - not a near-miss re-derivation of
       it. The per-bucket series below is only ever about SHAPE. */
    const totalsByWs = {};
    wsIds.forEach((id) => {
      const defs = (companyKpis && companyKpis[id]) || [];
      const { perKpi } = sumScores(scoresByWs[id], from, metaByIdFromDefs(defs), to);
      totalsByWs[id] = perKpi;
    });

    /* -- Revenue -------------------------------------------------------- */
    const revenueLive = !!revKpi;
    const revenue = revenueLive
      ? num(totalsByWs[revKpi.workspaceId]?.[revKpi.kpiId]?.amt)
      : typedRevenue;

    const revenueByBucket = buckets.map(() => 0);
    if (revenueLive && buckets.length) {
      const metaById = metaByIdFromDefs((companyKpis && companyKpis[revKpi.workspaceId]) || []);
      const perBucket = buckets.map(() => ({}));
      (scoresByWs[revKpi.workspaceId] || []).forEach((score) => {
        sortDaysAsc(score.days).forEach((day) => {
          const i = bucketOf(day.day);
          if (i == null) return;
          foldDayKpis(perBucket[i], day.kpis, metaById);
        });
      });
      perBucket.forEach((acc, i) => { revenueByBucket[i] = num(acc[revKpi.kpiId]?.amt); });
    } else if (buckets.length) {
      /* A typed figure has no shape of its own - spread it by how long each
         bucket actually is rather than pretending to a daily pattern. */
      buckets.forEach((b, i) => { revenueByBucket[i] = typedRevenue * evenShape[i]; });
    }

    /* -- Labor, department by department -------------------------------- */
    /* The totals (hours, cost, pay/hour, live) are NOT re-derived here — they
       are read straight off `laborData`, the exact same useLaborData hook
       Payroll vs Revenue itself renders from, called on that card's own saved
       options for this card's own window. Two independent copies of the
       roster/avg-pay/monthly-rate math drifted apart in practice (a decimal
       here, a filter edge-case there) and the two cards printed different
       payroll totals for the same month on the same board — reading the one
       real computation instead of a lookalike is what makes that impossible
       going forward. Only the per-bucket SHAPE (which day the dollars land
       on, for the chart) is still folded locally from raw hours; a difference
       there can only move dollars between buckets, never change how many
       there are, since every bucket is scaled to sum back to laborRow.cost. */
    const rosterByWs = {};
    wsIds.forEach((id) => {
      rosterByWs[id] = new Set(
        (allUsers || [])
          .filter((u) => String(u?.groupId || "") === id && isRealEmployee(u))
          .map((u) => String(u.userId || u._id || "")),
      );
    });

    /* Hours per workspace per bucket, folded ONCE and read by every row that
       counts that workspace - a Technicians + Office row must not walk
       Technicians' day rows twice and bill them twice. Shape only (see above)
       — the roster-vs-raw preference here only has to be reasonable, not
       exact, since it never sets a total by itself. */
    const hoursByWs = {};
    wsIds.forEach((id) => {
      const hKpiId = hoursWorkedKpiOf(companyKpis, id);
      if (!hKpiId) return;
      const roster = rosterByWs[id];
      const per = buckets.map(() => 0);
      const perAll = buckets.map(() => 0);
      let sawRosterRow = false;
      (scoresByWs[id] || []).forEach((score) => {
        const ownerId = String(score.ownerId || score.userId || "");
        const onRoster = roster.has(ownerId);
        sortDaysAsc(score.days).forEach((day) => {
          const i = bucketOf(day.day);
          if (i == null) return;
          const cell = day.kpis?.[hKpiId];
          if (cell == null) return;
          const amt = num(cell.amt);
          perAll[i] += amt;
          if (!onRoster) return;
          sawRosterRow = true;
          per[i] += amt;
        });
      });
      hoursByWs[id] = { per: sawRosterRow ? per : perAll };
    });

    const laborRowByKey = {};
    (laborData.rows || []).forEach((lr) => { laborRowByKey[lr.key] = lr; });

    const departments = laborRows.map((r) => {
      const memberIds = workspaceIdsOf(r);
      const memberNodes = memberWsOf(memberIds, includesSubWorkspaces(r));
      const laborRow = laborRowByKey[r.key];

      const hours = laborRow ? laborRow.hours : num(r.hours);
      const cost = laborRow ? laborRow.cost : num(r.totalCost);
      const live = laborRow ? laborRow.live : false;

      const hoursByBucket = buckets.map(() => 0);
      if (live) {
        memberNodes.forEach((mw) => {
          const h = hoursByWs[mw.id];
          if (!h) return;
          h.per.forEach((v, i) => { hoursByBucket[i] += v; });
        });
      } else {
        buckets.forEach((b, i) => { hoursByBucket[i] = hours * evenShape[i]; });
      }

      /* Every bucket is a SHARE of the authoritative total above, never a
         total of its own — that's what keeps the chart's daily/weekly shape
         from ever being able to disagree with the card's headline number. */
      const shape = hoursByBucket.reduce((s, v) => s + v, 0);
      const costByBucket = buckets.map((b, i) => (shape > 0
        ? cost * (hoursByBucket[i] / shape)
        : cost * evenShape[i]));

      const label = memberIds.length > 1
        ? (r.label?.trim() || "Others")
        : (wsNameById[memberIds[0]] || "Department");

      return {
        key: r.key,
        label,
        hours,
        cost,
        costByBucket,
        live,
        payPerHour: laborRow ? laborRow.payPerHour : null,
        goalPct: num(r.goalPct),
      };
    });

    /* -- Expenses, split so nothing is counted twice -------------------- */
    const items = expensesOf(settings).map((e) => ({ ...e, total: expenseAmount(e, revenue, days) }));
    const opexItems = items.filter((e) => e.kind !== "payroll");
    const payrollItems = items.filter((e) => e.kind === "payroll");
    const opexTotal = opexItems.reduce((s, e) => s + e.total, 0);
    const payrollExtra = payrollItems.reduce((s, e) => s + e.total, 0);

    /* A typed cost has no daily shape of its own, so it's spread by the
       SHAPE that matches what it actually is: opex (rent, fuel, insurance)
       keeps accruing over the weekend with nobody there to spend it, so it
       stays on the flat calendar shape; payroll extras (employer taxes,
       benefits, workers' comp, bonus pool) ride with the rest of Labor on
       the working-day shape, so they dip at the weekend same as wages do.
       "% of revenue" is the one exception to either: it tracks revenue by
       definition, so it follows revenue's shape instead. */
    const spreadTyped = (list, i, shape) => list.reduce((sum, e) => {
      if (e.unit === "%") return sum + revenueByBucket[i] * (num(e.amount) / 100);
      return sum + e.total * shape[i];
    }, 0);
    const expenseByBucket = buckets.map((b, i) => spreadTyped(opexItems, i, calendarShape));
    const payrollExtraByBucket = buckets.map((b, i) => spreadTyped(payrollItems, i, evenShape));

    /* -- Labor totals --------------------------------------------------- */
    const manualWages = expenseAmount(
      { amount: settings?.laborManual, unit: settings?.laborManualUnit || "$" },
      revenue,
      days,
    );
    /* laborData.totalCost, never departments.reduce(...) — the two are equal
       whenever every row resolved (the normal case), but reading the sum
       straight off laborData is what keeps this figure pinned to Payroll vs
       Revenue's own total even for a row this card can't otherwise resolve. */
    const wages = laborMode === "manual"
      ? manualWages
      : laborData.totalCost;
    const labor = wages + payrollExtra;

    const wagesByBucket = buckets.map((b, i) => {
      if (laborMode === "manual") {
        return settings?.laborManualUnit === "%"
          ? revenueByBucket[i] * (num(settings?.laborManual) / 100)
          : wages * evenShape[i];
      }
      return departments.reduce((s, d) => s + d.costByBucket[i], 0);
    });
    const laborByBucket = buckets.map((b, i) => wagesByBucket[i] + payrollExtraByBucket[i]);

    /* -- Working days, and the pace they set ----------------------------- */
    /* The denominator the boss asked for: the WHOLE period's working days,
       known on the 10th of the month as surely as on the 30th. Measured to
       the PERIOD's end, not the chart's - a "this week" card draws Monday to
       today but its goal is still for five days, and dividing by the three
       gone would inflate the pace by two thirds. */
    const periodEnd = periodEndFor(bucket, to, custom);
    const workingDays = workingDaysBetween(from, periodEnd, holidaySet);
    const workingDaysElapsed = workingDaysBetween(from, to, holidaySet);
    /* How much of the period has been worked - what a money goal is pro-rated
       by before a tile judges it (goalStatus above). */
    const paceElapsed = workingDays > 0 ? Math.min(1, workingDaysElapsed / workingDays) : 0;

    /* Goal ÷ total working days = daily pace. Only a Revenue goal typed in
       DOLLARS can set one: a goal typed as a share of revenue would be pacing
       revenue against itself, and there is no such figure to chase. */
    const revenueGoal = goalOf(settings, "revenue");
    const paceGoal = revenueGoal.on && revenueGoal.unit === "$"
      ? numOrNull(revenueGoal.limit)
      : null;
    const dailyPace = paceGoal != null && workingDays > 0 ? paceGoal / workingDays : null;
    /* A day bucket carries the daily pace flat, weekends included - the line
       answers "what does a day of trading have to bring", and a sawtooth that
       drops to zero every Saturday would be unreadable next to it. A week or
       month bucket is worth its OWN working days, so a full week asks for five
       days of pace and a holiday-shortened one asks for four. */
    const paceByBucket = buckets.map((b) => {
      if (dailyPace == null) return null;
      return grain === "day" ? dailyPace : dailyPace * workingDaysBetween(b.from, b.to, holidaySet);
    });

    /* -- The series the chart draws ------------------------------------- */
    const share = (part, whole) => (whole > 0 ? (part / whole) * 100 : null);
    const series = buckets.map((b, i) => {
      const rev = revenueByBucket[i];
      const exp = expenseByBucket[i];
      const lab = laborByBucket[i];
      /* A day that hasn't happened is null, never 0: zero is a claim that
         nothing was earned, and it would drag every line down to the axis for
         the rest of the month. Null simply ends each line at today and leaves
         the pace line running on alone, which is the whole point of drawing
         out to the 30th. */
      if (b.future) {
        return {
          key: b.key,
          label: b.label,
          full: b.full,
          future: true,
          revenue: null,
          expenses: null,
          labor: null,
          net: null,
          revenuePct: null,
          expensesPct: null,
          laborPct: null,
          netPct: null,
          pace: paceByBucket[i],
          depts: [],
        };
      }
      return {
        pace: paceByBucket[i],
        key: b.key,
        label: b.label,
        full: b.full,
        revenue: rev,
        expenses: exp,
        labor: lab,
        net: rev - exp - lab,
        revenuePct: rev > 0 ? 100 : null,
        expensesPct: share(exp, rev),
        laborPct: share(lab, rev),
        netPct: share(rev - exp - lab, rev),
        /* What the hover shows: each department's own share of THIS bucket's
           revenue - the breakdown drawn under the pointer in the sketch.
           `laborShare` is a second cut of the same number - share of THIS
           bucket's LABOR instead of its revenue - for the "click a point on
           Labor" drill-in, where the question is "who made up this day's
           labor bill", not "how big was this department against revenue". */
        depts: departments.map((d) => ({
          key: d.key,
          label: d.label,
          cost: d.costByBucket[i],
          pct: share(d.costByBucket[i], rev),
          laborShare: share(d.costByBucket[i], lab),
        })),
      };
    });

    const net = revenue - opexTotal - labor;

    return {
      grain,
      series,
      buckets,
      /* The range the chart DRAWS (`to` below is still where the figures
         stop), and the working days in it - what the header states and what
         the pace is divided by. */
      displayTo: axisTo,
      periodEnd,
      workingDays,
      workingDaysElapsed,
      paceElapsed,
      /* Both halves of the pace: the goal it came from, which the legend
         names, and the per-day figure, which is what the line is drawn at. */
      paceGoal,
      periodWord: periodWordFor(bucket),
      dailyPace,
      revenue,
      revenueLive,
      expenses: opexTotal,
      expenseItems: opexItems,
      payrollItems,
      payrollExtra,
      wages,
      labor,
      net,
      revenuePct: revenue > 0 ? 100 : null,
      expensesPct: share(opexTotal, revenue),
      laborPct: share(labor, revenue),
      netPct: share(net, revenue),
      departments,
      from,
      to,
      days,
      loading: fetching && !hasScores,
      refreshing: fetching && hasScores,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(laborRows),
    JSON.stringify(expensesOf(settings)),
    settings?.grain,
    /* Read directly in the bucket fold above (line ~625) — missing here meant
       toggling Setup > Chart > Weekends never recomputed the memo while the
       card was live-previewing (nothing else in this list changes), so the
       axis only ever picked up the new value on the next full remount
       (closing and reopening the card) — looking like the toggle "didn't
       work" and, since nobody saw it change, "didn't save" either. */
    settings?.workingDaysOnly,
    settings?.laborMode,
    settings?.laborManual,
    settings?.laborManualUnit,
    typedRevenue,
    revKpi?.workspaceId,
    revKpi?.kpiId,
    scoresByWs,
    companyKpis,
    bucket,
    customKey,
    fetching,
    hasScores,
    allUsers,
    subWsByParent,
    wsNameById,
    laborData,
    holidays,
    JSON.stringify(settings?.goals?.revenue || null),
  ]);
}
